/**
 * ThinkingPlanCard — Structured plan card with collapsible steps and log viewer.
 *
 * Replaces StepsGuideModule for playground chat. Renders a plan card per agent round
 * with status-aware step items, fold/unfold per step, and a monospace log viewer
 * for active/expanded steps.
 *
 * Data slots (from useManaged Agent runnerResults thinking card):
 *   - title: string — current activity context
 *   - steps: array — step objects with { label, content, status, humanizedEmoji, humanizedText, ts }
 *   - status: string — "active" | "done" | "error" | "cancelled"
 *   - conclusion: string — optional final text
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Status configuration ──
const STATUS_CONFIG = {
  thinking: { icon: '💭', color: 'rgba(88,86,214,0.7)', label: 'thinking' },
  tool_use: { icon: '🔧', color: 'rgba(255,149,0,0.8)', label: 'tool_use' },
  text: { icon: '✏️', color: 'rgba(52,199,89,0.7)', label: 'text' },
  speech: { icon: '🗣️', color: 'rgba(59,130,246,0.8)', label: 'speech' },
  status: { icon: '📋', color: 'rgba(142,142,147,0.5)', label: 'status' },
  done: { icon: '✅', color: 'rgba(52,199,89,0.8)', label: 'done' },
  error: { icon: '❌', color: 'rgba(255,59,48,0.8)', label: 'error' },
  cancelled: { icon: '⏹', color: 'rgba(142,142,147,0.6)', label: 'cancelled' },
  // The fast runner stamps these statuses on its steps; without them every step
  // fell through to the `thinking` badge (the reason every row read "thinking").
  in_progress: { icon: '⏳', color: 'rgba(88,86,214,0.7)', label: 'running' },
  running: { icon: '⏳', color: 'rgba(88,86,214,0.7)', label: 'running' },
  pending: { icon: '◦', color: 'rgba(142,142,147,0.6)', label: 'pending' },
  success: { icon: '✅', color: 'rgba(52,199,89,0.8)', label: 'done' },
};

const PLAN_STATUS_CONFIG = {
  active: { bg: 'rgba(88,86,214,0.08)', border: 'rgba(88,86,214,0.15)', dot: '#5856d6' },
  done: { bg: 'rgba(52,199,89,0.06)', border: 'rgba(52,199,89,0.12)', dot: '#34c759' },
  error: { bg: 'rgba(255,59,48,0.06)', border: 'rgba(255,59,48,0.12)', dot: '#ff3b30' },
  cancelled: { bg: 'rgba(142,142,147,0.06)', border: 'rgba(142,142,147,0.12)', dot: '#8e8e93' },
};

/** Shorten raw tool labels to be user-friendly */
function shortenLabel(label) {
  if (!label) return 'Working...';
  if (label.startsWith('Read:') || label.startsWith('Read ')) return 'Reading files';
  if (label.startsWith('Edit:') || label.startsWith('Edit ')) return 'Editing';
  if (label.startsWith('Write:') || label.startsWith('Write ')) return 'Writing';
  if (label.startsWith('Bash:') || label.startsWith('Bash ')) return 'Running command';
  if (label.startsWith('Grep:') || label.startsWith('Grep ')) return 'Searching';
  if (label.startsWith('Glob:') || label.startsWith('Glob ')) return 'Finding files';
  if (label.startsWith('WebFetch') || label.startsWith('WebSearch')) return 'Searching web';
  const cleaned = label.replace(/\/[\w/.:-]+/g, '').trim();
  const words = cleaned.split(/\s+/).slice(0, 5).join(' ');
  return words || 'Working...';
}

/** Normalize a step to object form — handles plain strings from agent-published cards */
function normalizeStep(step) {
  if (typeof step === 'string') {
    return { label: step, content: '', status: 'done' };
  }
  return step;
}

/** Parse steps from various formats (same as StepsGuideModule) */
function parseSteps(raw) {
  let steps;
  if (Array.isArray(raw)) {
    steps = raw;
  } else if (typeof raw !== 'string' || !raw.trim()) {
    return [];
  } else {
    try {
      const parsed = JSON.parse(raw);
      steps = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      try {
        const parts = raw.replace(/\}\s*\{/g, '}|||{').split('|||');
        steps = parts.map(p => JSON.parse(p)).filter(Boolean);
      } catch {
        return [];
      }
    }
  }
  return steps.map(normalizeStep);
}

// ── StepLogViewer — monospace log content for a single step ──
function StepLogViewer({ content }) {
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [content]);

  if (!content) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      style={{ overflow: 'hidden' }}
    >
      <div
        ref={logRef}
        style={{
          marginTop: 6,
          padding: '8px 10px',
          borderRadius: 6,
          background: 'rgba(0,0,0,0.03)',
          fontSize: 11,
          fontFamily: "'SF Mono', 'Cascadia Code', 'JetBrains Mono', monospace",
          color: 'rgba(0,0,0,0.45)',
          lineHeight: 1.6,
          maxHeight: 200,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {content}
      </div>
    </motion.div>
  );
}

// ── ToolEntryRow / ToolItemThumb — developer-facing tool-use detail (web) ──
// The runner's tool-activity tracker publishes a typed `data.tools[]` channel
// onto this same thinking-process card. Each tool entry renders as ONE item in
// the interleaved thinking timeline (see `timeline` below) — the web exposes the
// FULL technical detail: kind, raw input/result, per-item fields, correlation id.
// SSOT — the same array both surfaces read.
const TOOL_STATUS = {
  running: { dot: '#5856d6', label: 'running', spin: true },
  done: { dot: '#34c759', label: 'done', spin: false },
  error: { dot: '#ff3b30', label: 'error', spin: false },
};

function ToolItemThumb({ item }) {
  const [open, setOpen] = useState(false);
  const isVideo = item.media_kind === 'video';
  const pending = item.status === 'pending';
  const error = item.status === 'error';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 84 }}>
      <div
        onClick={() => setOpen(o => !o)}
        title={item.title || ''}
        style={{
          position: 'relative', width: 84, height: 64, borderRadius: 6,
          overflow: 'hidden', cursor: 'pointer',
          border: '1px solid rgba(0,0,0,0.08)',
          background: 'rgba(0,0,0,0.04)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {pending && (
          <span style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid rgba(88,86,214,0.7)', borderTopColor: 'transparent',
            animation: 'thinkingSpin 0.8s linear infinite',
          }} />
        )}
        {!pending && item.image_url && (
          <img src={item.image_url} alt={item.title || ''} loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
        {!pending && !item.image_url && (
          <span style={{ fontSize: 18, opacity: 0.5 }}>{error ? '⚠️' : isVideo ? '🎬' : '🔗'}</span>
        )}
        {isVideo && !pending && (
          <span style={{ position: 'absolute', right: 3, bottom: 3, fontSize: 11 }}>▶</span>
        )}
      </div>
      {(item.title || item.price) && (
        <div style={{ fontSize: 9.5, lineHeight: 1.25, color: 'rgba(0,0,0,0.55)', maxWidth: 84 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
          {item.price && <div style={{ color: '#34c759', fontWeight: 600 }}>{item.price}</div>}
        </div>
      )}
      <AnimatePresence>
        {open && (
          <StepLogViewer content={JSON.stringify(item, null, 2)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function ToolEntryRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = TOOL_STATUS[entry.status] || TOOL_STATUS.running;
  const items = Array.isArray(entry.items) ? entry.items : [];
  const raw = JSON.stringify({ tool_name: entry.tool_name, input: entry.input, summary: entry.summary, error: entry.error }, null, 2);
  return (
    <div style={{ padding: '6px 8px', borderRadius: 7, background: 'rgba(0,0,0,0.025)', marginBottom: 6 }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
        {cfg.spin ? (
          <span style={{ width: 8, height: 8, borderRadius: '50%', border: `2px solid ${cfg.dot}`, borderTopColor: 'transparent', animation: 'thinkingSpin 0.8s linear infinite', flexShrink: 0 }} />
        ) : (
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(0,0,0,0.65)' }}>{entry.label || entry.tool_name}</span>
        <span style={{ fontSize: 9, fontFamily: "'SF Mono', monospace", color: 'rgba(0,0,0,0.35)', background: 'rgba(88,86,214,0.1)', padding: '1px 5px', borderRadius: 3 }}>{entry.kind}</span>
        <span style={{ fontSize: 9, fontFamily: "'SF Mono', monospace", color: cfg.dot, marginLeft: 'auto' }}>{cfg.label}</span>
        <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.25)', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
      </div>
      {entry.query && (
        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', margin: '3px 0 0 15px', fontFamily: "'SF Mono', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.query}</div>
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 7 }}>
          {items.map((it, i) => <ToolItemThumb key={i} item={it} />)}
        </div>
      )}
      {entry.summary && (
        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.5)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto' }}>{entry.summary}</div>
      )}
      <AnimatePresence>{expanded && <StepLogViewer content={raw} />}</AnimatePresence>
    </div>
  );
}

// ── PlanStepItem — single step with status, label, collapsible log ──
/** Format epoch-ms timestamp to HH:MM:SS AM/PM */
function formatStepTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m}:${s} ${ampm}`;
}

function PlanStepItem({ step, isActive, isLast, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Auto-expand active step, auto-collapse when done
  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  const statusCfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.thinking;
  // Backend humanizeToolUse() produces "emoji Action" labels — split emoji from text
  const labelParts = (step.label || '').match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F?)\s*(.*)/u);
  const emoji = step.humanizedEmoji || (labelParts ? labelParts[1] : null) || statusCfg.icon || '•';
  const labelText = labelParts ? labelParts[2] : step.label;
  const displayText = step.humanizedText || labelText || shortenLabel(step.content) || 'Working...';
  // Dev surface: the expandable shows the FULL tool args (every field — e.g. an image-edit's
  // prompt AND its media:// ref), falling back to the one-field `content` summary.
  const detailBody = step.args_json || step.content;
  const hasContent = detailBody && detailBody.length > 0;
  const timeStr = formatStepTime(step.ts);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
    >
      <div
        onClick={() => hasContent && setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '5px 8px', borderRadius: 6,
          cursor: hasContent ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (hasContent) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Status indicator */}
        <span style={{
          fontSize: 13, lineHeight: '20px', flexShrink: 0,
          opacity: isActive ? 1 : 0.6,
        }}>
          {emoji}
        </span>

        {/* Step text + badge + timestamp */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {/* Status badge */}
            <span style={{
              fontSize: 9,
              fontFamily: "'SF Mono', 'Cascadia Code', monospace",
              padding: '1px 5px',
              borderRadius: 3,
              background: statusCfg.color.replace(/[\d.]+\)$/, '0.12)'),
              color: statusCfg.color,
              fontWeight: 600,
              letterSpacing: '0.02em',
              lineHeight: '16px',
              flexShrink: 0,
            }}>
              {statusCfg.label}
            </span>

            {/* Timestamp */}
            {timeStr && (
              <span style={{
                fontSize: 9,
                fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                color: 'rgba(0,0,0,0.25)',
                lineHeight: '16px',
                flexShrink: 0,
              }}>
                {timeStr}
              </span>
            )}

            {/* Raw tool name — DEV ONLY (the playground is developer-facing). The
                app never renders `step.tool`; users see only `humanizedText`. */}
            {step.tool && (
              <span style={{
                fontSize: 9,
                fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                color: 'rgba(0,0,0,0.45)',
                background: 'rgba(255,149,0,0.12)',
                padding: '1px 5px',
                borderRadius: 3,
                lineHeight: '16px',
                flexShrink: 0,
              }}>
                {step.tool}
              </span>
            )}
          </div>

          {/* Step label */}
          <span style={{
            fontSize: 12.5,
            lineHeight: '20px',
            color: isActive ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)',
            fontWeight: isActive ? 500 : 400,
          }}>
            {displayText}
          </span>

          {/* Expandable log viewer */}
          <AnimatePresence>
            {expanded && hasContent && (
              <StepLogViewer content={detailBody} />
            )}
          </AnimatePresence>
        </div>

        {/* Expand indicator */}
        {hasContent && (
          <span style={{
            fontSize: 10, color: 'rgba(0,0,0,0.2)', lineHeight: '20px',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}>
            ▼
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ── SessionIdBadge — click-to-copy Anthropic session id ──
// The runner stamps `managed_agent_session_id` into the "Session" step's
// metadata (managed-session-manager.ts appendThinkingStep). Surfacing it as a
// header badge gives the FULL id (Loki/Charlotte queries need it whole) plus a
// one-click copy affordance — the in-row text alone forced manual selection of
// a truncated string.
function SessionIdBadge({ sessionId }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback((e) => {
    e.stopPropagation(); // don't toggle the card's collapse
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1400); };
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = sessionId;
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch { /* clipboard unavailable */ }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(sessionId).then(done).catch(fallback);
    } else {
      fallback();
    }
  }, [sessionId]);

  return (
    <span
      onClick={copy}
      title={copied ? 'Copied!' : `Click to copy session id\n${sessionId}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 9.5, fontFamily: "'SF Mono', 'Cascadia Code', monospace",
        color: copied ? '#34c759' : 'rgba(88,86,214,0.9)',
        background: copied ? 'rgba(52,199,89,0.12)' : 'rgba(88,86,214,0.1)',
        padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
        border: `1px solid ${copied ? 'rgba(52,199,89,0.3)' : 'rgba(88,86,214,0.2)'}`,
        userSelect: 'none', transition: 'all 0.15s', lineHeight: '14px',
        // Show the FULL id (no truncation); let it wrap rather than clip.
        whiteSpace: 'normal', wordBreak: 'break-all', textAlign: 'left',
      }}
    >
      {copied ? '✓ Copied' : <>🔖&nbsp;{sessionId}</>}
    </span>
  );
}

// ── ThinkingPlanCard — main component ──
export default function ThinkingPlanCard({ data, cardStatus }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!data) return null;

  const steps = parseSteps(data.steps);
  const tools = Array.isArray(data.tools) ? data.tools : [];
  // The Anthropic session id rides the "Session" step's metadata. Surface the
  // first one found as the header badge (one per round).
  const sessionId = steps.reduce(
    (acc, s) => acc || (s && (s.managed_agent_session_id || s.managedAgentSessionId)) || null,
    null,
  );
  const planStatus = data.status || (cardStatus === 'finalized' ? 'done' : 'active');
  const planCfg = PLAN_STATUS_CONFIG[planStatus] || PLAN_STATUS_CONFIG.active;
  const isActive = planStatus === 'active';
  const isDone = planStatus === 'done';
  const title = data.title || (isDone ? 'Complete' : 'Thinking...');

  // Interleave steps + tool entries into ONE time-ordered timeline — tool
  // activity is one of the items, not a block before/after the steps. Steps
  // carry `ts` (ms); tool entries carry `started_at` (ms).
  const timeline = [
    ...steps.map((s, i) => ({ t: Number(s.ts) || 0, kind: 'step', data: s, key: `s${i}-${s.ts || ''}` })),
    ...tools.map((e, i) => ({ t: Number(e.started_at) || 0, kind: 'tool', data: e, key: `t${e.id || i}` })),
  ].sort((a, b) => a.t - b.t);

  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${planCfg.border}`,
      background: planCfg.bg,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Status dot / spinner */}
        {isActive ? (
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            border: `2px solid ${planCfg.dot}`,
            borderTopColor: 'transparent',
            animation: 'thinkingSpin 0.8s linear infinite',
            flexShrink: 0,
          }} />
        ) : (
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: planCfg.dot, flexShrink: 0,
          }} />
        )}

        {/* Title */}
        <span style={{
          fontSize: 12, fontWeight: 600,
          color: 'rgba(0,0,0,0.6)',
          flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>

        {/* Session id badge — click to copy the full Anthropic session id */}
        {sessionId && <SessionIdBadge sessionId={sessionId} />}

        {/* Step / tool count */}
        <span style={{
          fontSize: 10, color: 'rgba(0,0,0,0.3)',
          fontFamily: "'SF Mono', monospace",
        }}>
          {tools.length > 0 && `${tools.length} tool${tools.length !== 1 ? 's' : ''} · `}
          {steps.length} step{steps.length !== 1 ? 's' : ''}
        </span>

        {/* Collapse indicator */}
        <span style={{
          fontSize: 10, color: 'rgba(0,0,0,0.25)',
          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>
          ▼
        </span>
      </div>

      {/* Body: tool activity (typed) + thinking steps (narrative) */}
      <AnimatePresence>
        {!collapsed && (steps.length > 0 || tools.length > 0) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            style={{ overflow: 'hidden' }}
          >
            {/* One time-ordered timeline: narrative steps AND typed tool
                activity interleaved — each is one item, in chronological order. */}
            <div style={{ padding: '2px 6px 8px', borderTop: `1px solid ${planCfg.border}` }}>
              {timeline.map((item, idx) => item.kind === 'step' ? (
                <PlanStepItem
                  key={item.key}
                  step={item.data}
                  isActive={isActive && idx === timeline.length - 1}
                  isLast={idx === timeline.length - 1}
                  defaultExpanded={isActive && idx === timeline.length - 1}
                />
              ) : (
                <ToolEntryRow key={item.key} entry={item.data} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline CSS for spinner animation */}
      <style>{`
        @keyframes thinkingSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
